/**
 * ReactiveDayNutritionSummary Component
 * Automatically calculates and displays nutrition totals for an entire day
 * Updates reactively when any ingredient in any recipe changes
 */

import React, { useMemo } from "react";
import { OutgoingSearchResult } from "../types";
import { LLMMealPlanOutgoing } from "../types/mealPlanning";
import { NutritionSummaryCard } from "./NutritionSummaryCard";

interface ReactiveDayNutritionSummaryProps {
    dayData: LLMMealPlanOutgoing;
    title?: string;
    variant?: "compact" | "standard" | "detailed";
    className?: string;
    style?: React.CSSProperties;
}

export const ReactiveDayNutritionSummary: React.FC<
    ReactiveDayNutritionSummaryProps
> = ({
    dayData,
    title = "Daily Nutrition Total",
    variant = "standard",
    className,
    style,
}) => {
    // Create a dependency string that changes when any nutrition data changes
    const nutritionDependency = useMemo(() => {
        const mealTypes: (keyof LLMMealPlanOutgoing)[] = [
            "breakfast",
            "lunch",
            "dinner",
            "snack",
        ];

        return mealTypes
            .map((mealType) => {
                if (mealType === "macros") return "";
                const mealSlot = dayData[mealType] as { recipes: any[] };
                const recipes = mealSlot?.recipes || [];

                return recipes
                    .map((recipe) => {
                        const ingredients: OutgoingSearchResult[] =
                            recipe.ingredients || [];
                        return ingredients
                            .map((ing) => {
                                const n = ing.nutritionPreview;
                                return `${ing.resultId}-${n?.calories || 0}-${
                                    n?.protein || 0
                                }-${n?.carbs || 0}-${n?.fat || 0}-${
                                    n?.fiber || 0
                                }`;
                            })
                            .join(",");
                    })
                    .join(";");
            })
            .join("|");
    }, [dayData]);

    // Automatically calculate nutrition totals from all ingredients in all recipes for the day
    // This will recalculate whenever any ingredient in any recipe changes
    const { nutritionTotals, totalIngredients } = useMemo(() => {
        const mealTypes: (keyof LLMMealPlanOutgoing)[] = [
            "breakfast",
            "lunch",
            "dinner",
            "snack",
        ];

        let ingredientCount = 0;
        const totals = mealTypes.reduce(
            (dayTotal, mealType) => {
                if (mealType === "macros") return dayTotal; // Skip the macros field itself

                const mealSlot = dayData[mealType] as { recipes: any[] };
                const recipes = mealSlot?.recipes || [];

                const mealNutrition = recipes.reduce(
                    (mealTotal, recipe) => {
                        const ingredients: OutgoingSearchResult[] =
                            recipe.ingredients || [];
                        ingredientCount += ingredients.length;

                        const recipeNutrition = ingredients.reduce(
                            (recipeTotal, ingredient) => {
                                const nutrition = ingredient.nutritionPreview;
                                return {
                                    calories:
                                        recipeTotal.calories +
                                        (nutrition?.calories || 0),
                                    protein:
                                        recipeTotal.protein +
                                        (nutrition?.protein || 0),
                                    carbs:
                                        recipeTotal.carbs +
                                        (nutrition?.carbs || 0),
                                    fat:
                                        recipeTotal.fat + (nutrition?.fat || 0),
                                    fiber:
                                        recipeTotal.fiber +
                                        (nutrition?.fiber || 0),
                                };
                            },
                            {
                                calories: 0,
                                protein: 0,
                                carbs: 0,
                                fat: 0,
                                fiber: 0,
                            }
                        );

                        return {
                            calories:
                                mealTotal.calories + recipeNutrition.calories,
                            protein:
                                mealTotal.protein + recipeNutrition.protein,
                            carbs: mealTotal.carbs + recipeNutrition.carbs,
                            fat: mealTotal.fat + recipeNutrition.fat,
                            fiber: mealTotal.fiber + recipeNutrition.fiber,
                        };
                    },
                    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
                );

                return {
                    calories: dayTotal.calories + mealNutrition.calories,
                    protein: dayTotal.protein + mealNutrition.protein,
                    carbs: dayTotal.carbs + mealNutrition.carbs,
                    fat: dayTotal.fat + mealNutrition.fat,
                    fiber: dayTotal.fiber + mealNutrition.fiber,
                };
            },
            { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
        );

        return {
            nutritionTotals: totals,
            totalIngredients: ingredientCount,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nutritionDependency]);

    return (
        <NutritionSummaryCard
            title={title}
            nutritionData={nutritionTotals}
            itemCount={totalIngredients}
            itemLabel="ingredients"
            variant={variant}
            className={className}
            style={style}
        />
    );
};
